'use client';

import debounce from 'lodash-es/debounce';
import cloneDeep from 'lodash-es/cloneDeep';
import { putLayout } from 'app/apis/layout';
import { LAYOUT_SECTION_NAME } from 'app/shop-ui/constants';
import { LayoutTransformer } from 'shop-ui/tools/LayoutTransformer';
import {
    type SliceCreator,
    type EditorStore,
    type EditorActionSlice,
    type UploadingMedia,
    type HistoryEntry,
} from './type';

export const UNDO_LIMIT = 15;

export const createEditorActionSlice: SliceCreator<EditorActionSlice, EditorStore> = (
    set,
    get
) => ({
    setIsMobile: (isMobile: boolean) => {
        set({ isMobile });
    },

    setGridResizing: (isGridResizing: boolean) => {
        set({ isGridResizing });
    },

    setCellResizing: (isCellResizing: boolean) => {
        set({ isCellResizing });
    },

    getFlatMap: () => get().flatMap,

    getStyleMap: () => get().styleMap,

    getContentMap: () => get().contentMap,

    getSiteStyle: () => get().siteStyle,

    getPageLayoutMeta: () => get().pageLayoutMeta,

    getRootSections: () => get().rootSections,

    getEditingElementId: () => get().editingElementId,

    getEditingLayoutId: () => get().editingLayoutId,

    getEditingSectionName: () => get().editingSectionName,

    getEditingElementType: () => get().editingElementType,

    getNextInsertSectionId: () => get().nextInsertSectionId,

    setEditingNodeChain: (nodes) => set({ editingNodeChain: nodes }),

    setDraggingElementId: (id) => set({ draggingElementId: id }),

    setDraggingType: (type) => set({ draggingType: type }),

    setEditingElementId: (id) => set({ editingElementId: id }),

    setHoverElementId: (id) => set({ hoverElementId: id }),

    setEditingLayoutId: (id) => set({ editingLayoutId: id }),

    setEditingElementType: (type) => set({ editingElementType: type }),

    setNextInsertSectionId: (id) => set({ nextInsertSectionId: id }),

    setEditingSectionName: (sectionName) => set({ editingSectionName: sectionName }),

    setIsSaving: (isSaving) => set({ isSaving }),

    setIsLoading: (isLoading) => set({ isLoading }),

    setHeaderHeight: (headerHeight) => set({ headerHeight }),

    setFooterHeight: (footerHeight) => set({ footerHeight }),

    setIsExternalDragging: (isExternalDragging) => set({ isExternalDragging }),

    setIsExternalDraggingSection: (isExternalDraggingSection) =>
        set({ isExternalDraggingSection }),

    setRootSections: (updater) => {
        set((state) => {
            const updates =
                typeof updater === 'function' ? updater(state.rootSections) : updater;

            const newRootSections = {
                ...state.rootSections,
                ...updates,
            };

            return { rootSections: newRootSections };
        });
    },
    setFlatMap: (updater) => {
        set((state) => {
            const updates =
                typeof updater === 'function' ? updater(state.flatMap) : updater;

            const newFlatMap = {
                ...state.flatMap,
                ...updates,
            };

            return { flatMap: newFlatMap };
        });
    },
    setStyleMap: (updater) => {
        set((state) => {
            const updates =
                typeof updater === 'function' ? updater(state.styleMap) : updater;

            const newStyleMap = {
                ...state.styleMap,
                ...updates,
            };

            return {
                styleMap: newStyleMap,
            };
        });
    },

    setContentMap: (updater) => {
        set((state) => {
            const updates =
                typeof updater === 'function' ? updater(state.contentMap) : updater;

            const newContentMap = {
                ...state.contentMap,
                ...updates,
            };

            return { contentMap: newContentMap };
        });
    },

    setSiteStyle: (updater) => {
        set((state) => {
            const updates =
                typeof updater === 'function' ? updater(state.siteStyle) : updater;

            const newSiteStyle = {
                ...state.siteStyle,
                ...updates,
            };

            return { siteStyle: newSiteStyle };
        });
    },

    setPageLayoutMeta: (updater) => {
        set((state) => {
            const updates =
                typeof updater === 'function' ? updater(state.pageLayoutMeta) : updater;

            const newPageLayoutMeta = {
                ...state.pageLayoutMeta,
                ...updates,
            };

            return { pageLayoutMeta: newPageLayoutMeta };
        });
    },

    setIsDirty: (isDirty) => set({ isDirty }),

    markDirty: (overrideSectionName?: LAYOUT_SECTION_NAME, overrideLayoutId?: string) => {
        const { editingSectionName } = get();

        const sectionName = overrideSectionName || editingSectionName;

        if (!sectionName) {
            console.error('section name is not found');

            return;
        }

        set({ isDirty: true, isSaving: true });

        get().debounceSave(sectionName, overrideLayoutId);
    },

    save: async (sectionName: LAYOUT_SECTION_NAME, overrideLayoutId?: string) => {
        const {
            setIsDirty,
            pageLayoutMeta,
            flatMap,
            styleMap,
            contentMap,
            siteStyle,
            rootSections,
            setIsSaving,
        } = get();

        setIsDirty(false);

        const meta = pageLayoutMeta[sectionName];

        if (!meta) {
            console.error('meta is not found');

            return;
        }

        const { layoutId, pageId } = meta;

        const transformedLayout = LayoutTransformer.toNestLayout(
            flatMap,
            rootSections[sectionName]
        );

        const layout = {
            layout: transformedLayout,
            style: styleMap,
            content: contentMap,
            site: siteStyle,
        };

        await putLayout({
            layoutId: overrideLayoutId ?? layoutId,
            section: sectionName,
            pageId,
            layout,
        });

        setIsSaving(false);
    },

    debounceSave: debounce(
        (sectionName: LAYOUT_SECTION_NAME, overrideLayoutId?: string) => {
            get().save(sectionName, overrideLayoutId);
        },
        300
    ),

    setUploadingMedia: (
        id: string,
        src: UploadingMedia['src'],
        status: UploadingMedia['status']
    ) => {
        set((state) => {
            const newUploadingMedia = { ...state.uploadingMedia };

            newUploadingMedia[id] = {
                src,
                status,
            };

            return { uploadingMedia: cloneDeep(newUploadingMedia) };
        });
    },

    getUploadingMedia: (id: string) => {
        return get().uploadingMedia[id] ?? false;
    },

    setNodeVersion: (id: string) => {
        set((state) => ({
            nodeVersionMap: {
                ...state.nodeVersionMap,
                [id]: new Date().getTime(),
            },
        }));
    },

    getHistoryStack: () => get().historyStack,

    getFutureStack: () => get().futureStack,

    setHistoryStack: (updater) => {
        set((state) => {
            const updates =
                typeof updater === 'function' ? updater(state.historyStack) : updater;

            if (updates.length > UNDO_LIMIT) {
                updates.shift();
            }

            return { historyStack: updates };
        });
    },

    setFutureStack: (updater) => {
        set((state) => {
            const updates =
                typeof updater === 'function' ? updater(state.futureStack) : updater;

            return { futureStack: updates };
        });
    },

    record: (entry: HistoryEntry) => {
        set((state) => ({
            historyStack: [...state.historyStack, entry],
            futureStack: [],
        }));
    },

    setIsCarouselUploading: (isCarouselUploading: boolean) => {
        set({ isCarouselUploading });
    },
});
